import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { Avatar } from '@thoughtspot/radiant-react/widgets/avatar';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import cx from 'classnames';
import _ from 'lodash';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Key } from 'w3c-keys';
import styles from './user-menu.module.scss';
import { UserV3ProfileDropdown } from './user-profile-dropdown';
export var UserMenuV3TestIds;
(function (UserMenuV3TestIds) {
    UserMenuV3TestIds["USER_PROFILE_AVATAR"] = "user-profile-avatar";
    UserMenuV3TestIds["USER_MENU_POPOVER"] = "user-menu-popover";
    UserMenuV3TestIds["USER_PROFILE_AVATAR_WITH_IN_APP"] = "user-profile-avatar-in-app";
})(UserMenuV3TestIds || (UserMenuV3TestIds = {}));
const MENU_ITEMS = {
    VIEW_PROFILE: 'view-profile',
    ADMIN_SETTINGS: 'admin-settings',
    SIGN_OUT: 'sign-out',
};
export const UserMenuV3 = ({ collectEvent, getUserDisplayName, getUserGuid, goToUserPreference, handleLogout, hasProfilePic, onMenuClose, userEmail, navService, sessionService, isOrgEnabled = false, inviteTeammates, }) => {
    const { t } = useTranslation();
    const avatarSrc = `/callosum/v1/image/profile/${getUserGuid()}`;
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [avatarKey, setAvatarKey] = useState(null);
    const profilePicRef = useRef(null);
    const profileSectionRef = useRef(null);
    const resetUserMenuState = () => {
        setShowProfileMenu(false);
        profilePicRef.current.focus();
        if (_.isFunction(onMenuClose)) {
            onMenuClose();
        }
    };
    const handleKeyDown = (event) => {
        if (event?.key === Key.Escape && showProfileMenu === true) {
            resetUserMenuState();
        }
    };
    useEffect(() => {
        setAvatarKey(hasProfilePic ? 'profilePic' : 'noProfilePic');
    }, [hasProfilePic]);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
    useEffect(() => {
        if (showProfileMenu) {
            profileSectionRef?.current?.focus();
        }
    }, [showProfileMenu]);
    const commonAvatarProps = {
        name: getUserDisplayName(),
        src: avatarSrc,
        namePosition: 'bottom',
        showTooltip: false,
    };
    const seeYourProfile = () => {
        collectEvent(new UserAction(userActionTypeEnum.CLICK_VIEW_PROFILE), {});
        collectEvent(new UserAction(userActionTypeEnum.USER_PROFILE_ICON_MENU_ITEM_CLICK), { menuItemName: MENU_ITEMS.VIEW_PROFILE });
        setShowProfileMenu(false);
        goToUserPreference();
    };
    const goToAdminSettings = () => {
        collectEvent(new UserAction(userActionTypeEnum.USER_PROFILE_ICON_MENU_ITEM_CLICK), { menuItemName: MENU_ITEMS.ADMIN_SETTINGS });
        if (sessionService.isOrgUiEnabled() &&
            sessionService.isOrgPerUrlEnabled()) {
            const { queryString } = getQueryParamsAndQueryString();
            const queryWithoutPrefix = queryString.replace(/^\?/, '');
            const queryStringWithoutOrgId = queryWithoutPrefix
                .replace(/(?:&|^)orgId=[^&]*/, '')
                .replace(/^&/, '');
            window.open(`${window.location.pathname}${queryStringWithoutOrgId ? `?${queryStringWithoutOrgId}` : ''}#/admin`, '_self');
            return;
        }
        navService.goToUrl('/admin');
    };
    const handleLogoutHandler = () => {
        collectEvent(new UserAction(userActionTypeEnum.USER_PROFILE_ICON_MENU_ITEM_CLICK), { menuItemName: MENU_ITEMS.SIGN_OUT });
        handleLogout();
    };
    const getV3UserMenuContent = () => {
        return (React.createElement(UserV3ProfileDropdown, { avatarKey: avatarKey, avatarSrc: avatarSrc, getUserDisplayName: getUserDisplayName, displayName: getUserDisplayName(), userEmail: userEmail, seeYourProfile: seeYourProfile, goToAdminSettings: goToAdminSettings, handleLogout: handleLogoutHandler, setShowProfileMenu: setShowProfileMenu, isAdminTabVisible: sessionService.isAdminTabVisible() &&
                !sessionService.isApplicationSwitcherHidden(), inviteTeammates: inviteTeammates }));
    };
    return (React.createElement(Suspense, { fallback: React.createElement(LoadingIndicator.Contextual, { className: styles.loadingIndicator }) },
        React.createElement("div", { className: cx(styles.avatarButtonProfileV3Container, {
                [styles.avatarButtonProfileV3Expanded]: showProfileMenu,
                [styles.avatarButtonProfileV3OrgsEnabled]: isOrgEnabled,
            }) },
            React.createElement(Popover, { className: styles.profilePopover, bottomOffset: 8, targetRef: profilePicRef, content: getV3UserMenuContent(), isVisible: showProfileMenu, align: "bottom-start", appendToBody: true, onClickOutside: () => {
                    resetUserMenuState();
                }, "data-testid": UserMenuV3TestIds.USER_MENU_POPOVER }),
            React.createElement("button", { type: "button", "aria-label": t('USER_PROFILE_HEADER', {
                    nameOfUser: getUserDisplayName(),
                }), tabIndex: 0, ref: profilePicRef, className: styles.avatarButtonProfileV3, onClick: () => {
                    collectEvent(new UserAction(userActionTypeEnum.USER_PROFILE_ICON_CLICK), {});
                    if (showProfileMenu) {
                        resetUserMenuState();
                    }
                    else {
                        setShowProfileMenu(true);
                    }
                }, "aria-expanded": !!showProfileMenu },
                React.createElement(Avatar, { ...commonAvatarProps, key: avatarKey, "data-testid": UserMenuV3TestIds.USER_PROFILE_AVATAR, className: styles.avator })))));
};
//# sourceMappingURL=user-menu-v3.js.map